import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiFileText, 
  FiArrowLeft, 
  FiSearch, 
  FiEye, 
  FiEdit, 
  FiUser,
  FiDollarSign,
  FiCalendar,
  FiFilter,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiMapPin,
  FiPhone,
  FiMail,
  FiBriefcase,
  FiTarget
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { applicationsAPI, categoriesAPI, usersAPI, banksAPI, statesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ImageUpload from '../components/ImageUpload';

// Constants
const PAGE_SIZE = 10;
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'under_review', label: 'Under Review', color: 'bg-blue-100 text-blue-800' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
  { value: 'disbursed', label: 'Disbursed', color: 'bg-purple-100 text-purple-800' }
];

const AgentApplications = () => {
  // Router and auth
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Application state
  const [applications, setApplications] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalApplications, setTotalApplications] = useState(0);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [viewingApplication, setViewingApplication] = useState(null);
  
  // Data state
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [states, setStates] = useState([]);

  // Effects
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAgentApplications();
    fetchCategories();
    fetchBanks();
    fetchStates();
  }, [agentId, currentPage, searchQuery, statusFilter]);

  // API Functions
  const fetchAgentApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getApplications(
        currentPage, 
        PAGE_SIZE, 
        searchQuery, 
        statusFilter, 
        agentId
      );
      
      if (response.success) {
        setApplications(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalApplications(response.pagination?.totalItems || 0);
        
        // Get agent info from first application if available
        if (response.data && response.data.length > 0 && response.data[0].createdBy) {
          setAgentInfo(response.data[0].createdBy);
        }
      }
    } catch (error) {
      console.error('Error fetching agent applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await banksAPI.getBanks();
      setBanks(response.data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await statesAPI.getStates();
      setStates(response.data || response || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getUsers(1, 100, '', agentId);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Event Handlers
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAgentApplications();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleView = (application) => {
    setViewingApplication(application);
    setEditingApplication(null);
    setShowModal(true);
  };

  const handleEdit = (application) => {
    setEditingApplication(application);
    setViewingApplication(null);
    setShowModal(true);
    fetchUsers(); // Fetch users for dropdown
  };

  const handleSave = async (applicationData) => {
    try {
      if (editingApplication) {
        // Check if status has changed
        const statusChanged = editingApplication.status !== applicationData.status;
        
        if (statusChanged && user?.role === 'admin') {
          // Use status update route for status changes
          const comment = applicationData.statusComment || `Status updated to ${applicationData.status} by admin`;
          await applicationsAPI.updateApplicationStatus(editingApplication._id, applicationData.status, comment);
        }
        
        // Update other fields using regular update route
        const { status, statusComment, ...otherFields } = applicationData;
        await applicationsAPI.updateApplication(editingApplication._id, otherFields);
        
        toast.success('Application updated successfully');
      }
      setShowModal(false);
      setEditingApplication(null);
      setViewingApplication(null);
      fetchAgentApplications();
    } catch (error) {
      console.error('Error saving application:', error);
      
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach(err => {
          toast.error(`${err.path}: ${err.msg}`, { duration: 4000 });
        });
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to save application');
      }
    }
  };

  // Utility Functions
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const statusOption = STATUS_OPTIONS.find(option => option.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FiClock className="w-3 h-3" />;
      case 'under_review': return <FiFileText className="w-3 h-3" />;
      case 'approved': return <FiCheckCircle className="w-3 h-3" />;
      case 'rejected': return <FiXCircle className="w-3 h-3" />;
      case 'disbursed': return <FiDollarSign className="w-3 h-3" />;
      default: return <FiClock className="w-3 h-3" />;
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/users')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft className="w-4 h-4" />
              <span>Back to Users</span>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Agent Applications</h1>
              {agentInfo && (
                <p className="text-sm text-gray-600">
                  Applications by {agentInfo.name} ({agentInfo.email})
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search applications..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FiSearch className="w-4 h-4" />
                <span>Search</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiFileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{totalApplications}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FiClock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {applications.filter(app => app.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiCheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900">
                {applications.filter(app => app.status === 'approved').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <FiXCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">
                {applications.filter(app => app.status === 'rejected').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-8">
            <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No applications found for this agent</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Application
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preferred Bank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applications.map((application) => (
                  <tr key={application._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FiFileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {application._id.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <FiUser className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {application.user?.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {application.user?.phone || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {application.category?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(application.loanAmount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {application.preferredBank || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                        {getStatusIcon(application.status)}
                        <span className="ml-1">{application.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(application.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(application)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="View"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(application)}
                          className="text-green-600 hover:text-green-900 transition-colors"
                          title="Edit"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Application Modal */}
      <AnimatePresence>
        {showModal && (
          <ApplicationModal
            application={editingApplication || viewingApplication}
            viewingApplication={viewingApplication}
            editingApplication={editingApplication}
            categories={categories}
            users={users}
            banks={banks}
            states={states}
            user={user}
            onSave={handleSave}
            onClose={() => {
              setShowModal(false);
              setEditingApplication(null);
              setViewingApplication(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Application Modal Component (copied from Applications.js)
const ApplicationModal = ({ application, viewingApplication, editingApplication, categories, users, banks, states, user, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    user: '',
    category: '',
    loanAmount: '',
    loanPurpose: '',
    monthlyIncome: '',
    employmentType: '',
    companyName: '',
    workExperience: '',
    aadharNumber: '',
    panNumber: '',
    preferredBank: '',
    status: 'pending', // Add status field
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    documents: {
      aadharCard: '',
      panCard: '',
      bankStatement: '',
      salarySlip: '',
      otherDocuments: []
    }
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (application) {
      setFormData({
        user: application.user?._id || application.user || '',
        category: application.category?._id || application.category || '',
        loanAmount: application.loanAmount || '',
        loanPurpose: application.loanPurpose || '',
        monthlyIncome: application.monthlyIncome || '',
        employmentType: application.employmentType || '',
        companyName: application.companyName || '',
        workExperience: application.workExperience || '',
        aadharNumber: application.aadharNumber || '',
        panNumber: application.panNumber || '',
        preferredBank: application.preferredBank || '',
        status: application.status || 'pending', // Set initial status
        address: {
          street: application.address?.street || '',
          city: application.address?.city || '',
          state: application.address?.state || '',
          pincode: application.address?.pincode || ''
        },
        documents: {
          aadharCard: application.documents?.aadharCard || '',
          panCard: application.documents?.panCard || '',
          bankStatement: application.documents?.bankStatement || '',
          salarySlip: application.documents?.salarySlip || '',
          otherDocuments: application.documents?.otherDocuments || []
        }
      });
    }
  }, [application]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'disbursed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <FiCheckCircle className="w-4 h-4" />;
      case 'pending': return <FiClock className="w-4 h-4" />;
      case 'under_review': return <FiTarget className="w-4 h-4" />;
      case 'rejected': return <FiXCircle className="w-4 h-4" />;
      case 'disbursed': return <FiCheckCircle className="w-4 h-4" />;
      default: return <FiClock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (viewingApplication) return; // Don't submit if just viewing

    setLoading(true);
    setErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName] || '';
  };

  const hasFieldError = (fieldName) => {
    return !!errors[fieldName];
  };

  const clearFieldError = (fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    clearFieldError(fieldName);
  };

  const handleAddressChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [fieldName]: value
      }
    }));
    clearFieldError(`address.${fieldName}`);
  };

  const handleDocumentChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [fieldName]: value
      }
    }));
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    if (filePath.startsWith('http')) return filePath;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/${filePath}`;
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {viewingApplication ? 'View Application' : editingApplication ? 'Edit Application' : 'New Application'}
            </h2>
            {application && (
              <div className="flex items-center space-x-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                  {getStatusIcon(application.status)}
                  <span className="ml-1">{application.status}</span>
                </span>
                <span className="text-sm text-gray-500">
                  {application.applicationNumber}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {viewingApplication ? (
            // View Mode
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Applicant</label>
                      <p className="mt-1 text-sm text-gray-900">{application.user?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <p className="mt-1 text-sm text-gray-900">{application.category?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Loan Amount</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(application.loanAmount)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Loan Purpose</label>
                      <p className="mt-1 text-sm text-gray-900">{application.loanPurpose}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Preferred Bank</label>
                      <p className="mt-1 text-sm text-gray-900">{application.preferredBank || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          {getStatusIcon(application.status)}
                          <span className="ml-1">{application.status}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Employment Type</label>
                      <p className="mt-1 text-sm text-gray-900">{application.employmentType}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Monthly Income</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(application.monthlyIncome)}</p>
                    </div>
                    {application.companyName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Company</label>
                        <p className="mt-1 text-sm text-gray-900">{application.companyName}</p>
                      </div>
                    )}
                    {application.workExperience && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Work Experience</label>
                        <p className="mt-1 text-sm text-gray-900">{application.workExperience} years</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Street</label>
                    <p className="mt-1 text-sm text-gray-900">{application.address?.street}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <p className="mt-1 text-sm text-gray-900">{application.address?.city}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <p className="mt-1 text-sm text-gray-900">{application.address?.state}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pincode</label>
                    <p className="mt-1 text-sm text-gray-900">{application.address?.pincode}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {application.documents?.aadharCard && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Aadhar Card</label>
                      <a
                        href={getFileUrl(application.documents.aadharCard)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <FiFileText className="w-4 h-4 mr-1" />
                        View Document
                      </a>
                    </div>
                  )}
                  {application.documents?.panCard && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">PAN Card</label>
                      <a
                        href={getFileUrl(application.documents.panCard)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <FiFileText className="w-4 h-4 mr-1" />
                        View Document
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(application.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(application.updatedAt)}</p>
                </div>
              </div>
            </div>
          ) : (
            // Edit Mode
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                  <select
                    value={formData.user}
                    onChange={(e) => handleFieldChange('user', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('user') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={user?.role === 'agent'} // Agents can't change user
                  >
                    <option value="">Select User</option>
                    {users.map(user => (
                      <option key={user._id} value={user._id}>
                        {user.name} - {user.phone}
                      </option>
                    ))}
                  </select>
                  {hasFieldError('user') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('user')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('category') ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {hasFieldError('category') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('category')}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Amount</label>
                  <input
                    type="number"
                    value={formData.loanAmount}
                    onChange={(e) => handleFieldChange('loanAmount', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('loanAmount') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    min="1000"
                    placeholder="Enter loan amount"
                  />
                  {hasFieldError('loanAmount') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('loanAmount')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Income</label>
                  <input
                    type="number"
                    value={formData.monthlyIncome}
                    onChange={(e) => handleFieldChange('monthlyIncome', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('monthlyIncome') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter monthly income"
                  />
                  {hasFieldError('monthlyIncome') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('monthlyIncome')}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loan Purpose</label>
                <textarea
                  value={formData.loanPurpose}
                  onChange={(e) => handleFieldChange('loanPurpose', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    hasFieldError('loanPurpose') ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows="3"
                  placeholder="Describe the purpose of the loan"
                />
                {hasFieldError('loanPurpose') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('loanPurpose')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Bank *</label>
                <select
                  value={formData.preferredBank}
                  onChange={(e) => handleFieldChange('preferredBank', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    hasFieldError('preferredBank') ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select Preferred Bank</option>
                  {banks.map(bank => (
                    <option key={bank._id} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </select>
                {hasFieldError('preferredBank') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('preferredBank')}</p>
                )}
              </div>

              {/* Status Field - Only for Admin */}
              {user?.role === 'admin' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleFieldChange('status', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        hasFieldError('status') ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="pending">Pending</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="disbursed">Disbursed</option>
                    </select>
                    {hasFieldError('status') && (
                      <p className="mt-1 text-sm text-red-600">{getFieldError('status')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status Comment (Optional)</label>
                    <input
                      type="text"
                      value={formData.statusComment || ''}
                      onChange={(e) => handleFieldChange('statusComment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add a comment for status change"
                    />
                  </div>
                </div>
              )}

              {/* Employment Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                  <select
                    value={formData.employmentType}
                    onChange={(e) => handleFieldChange('employmentType', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('employmentType') ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Employment Type</option>
                    <option value="salaried">Salaried</option>
                    <option value="self-employed">Self Employed</option>
                    <option value="business">Business</option>
                    <option value="student">Student</option>
                    <option value="other">Other</option>
                  </select>
                  {hasFieldError('employmentType') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('employmentType')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleFieldChange('companyName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('companyName') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter company name"
                  />
                  {hasFieldError('companyName') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('companyName')}</p>
                  )}
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        hasFieldError('address.street') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter street address"
                    />
                    {hasFieldError('address.street') && (
                      <p className="mt-1 text-sm text-red-600">{getFieldError('address.street')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        hasFieldError('address.city') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter city"
                    />
                    {hasFieldError('address.city') && (
                      <p className="mt-1 text-sm text-red-600">{getFieldError('address.city')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <select
                      value={formData.address.state}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        hasFieldError('address.state') ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state._id} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {hasFieldError('address.state') && (
                      <p className="mt-1 text-sm text-red-600">{getFieldError('address.state')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                    <input
                      type="text"
                      value={formData.address.pincode}
                      onChange={(e) => handleAddressChange('pincode', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        hasFieldError('address.pincode') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter pincode"
                      pattern="[0-9]{6}"
                    />
                    {hasFieldError('address.pincode') && (
                      <p className="mt-1 text-sm text-red-600">{getFieldError('address.pincode')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AgentApplications; 